import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  ArrowsClockwise,
  DownloadSimple,
  ShieldWarning,
  Prohibit,
  CheckCircle,
  Trash,
  ArrowCounterClockwise,
  X,
  Users as UsersIcon,
  Crown,
  PencilSimple,
  Notepad,
  ClockCounterClockwise,
  ListChecks,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";

const FILTERS = [
  { id: "", label: "All users" },
  { id: "active", label: "Active" },
  { id: "suspended", label: "Suspended" },
  { id: "blocked", label: "Blocked" },
  { id: "admins", label: "Admins" },
  { id: "recent_register", label: "Recently registered" },
  { id: "recent_active", label: "Recently active" },
  { id: "inactive_30", label: "Inactive 30+ days" },
];

const STATUS_BADGE = {
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  suspended: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  blocked: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
  deleted: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
  pending_verification: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
};

const ROLE_BADGE = {
  user: "bg-slate-500/10 text-slate-600 dark:text-slate-300",
  moderator: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  admin: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
};

function fmt(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function timeAgo(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return fmt(ts);
}

export default function UsersAdminPanel() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [openUserId, setOpenUserId] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { action, reason }

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await api.get("/admin/users/summary");
      setSummary(r.data);
    } catch {
      // soft fail
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (q.trim()) params.set("q", q.trim());
      if (filter) params.set("filter", filter);
      params.set("sort", sort);
      params.set("order", order);
      if (includeDeleted) params.set("include_deleted", "true");
      const r = await api.get(`/admin/users?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Couldn't load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, filter, sort, order, includeDeleted]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchList(); }, [fetchList]);

  // Reset to page 1 on filter/search/sort change.
  useEffect(() => { setPage(1); }, [q, filter, sort, order, includeDeleted]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((u) => u.id)));
  };

  const setSortField = (field) => {
    if (sort === field) setOrder(order === "desc" ? "asc" : "desc");
    else { setSort(field); setOrder("desc"); }
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (selected.size) params.set("ids", Array.from(selected).join(","));
      if (includeDeleted) params.set("include_deleted", "true");
      const r = await api.get(`/admin/users/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `voice-users-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.size || "all"} users`);
    } catch {
      toast.error("Export failed");
    }
  };

  const runBulk = async () => {
    if (!bulkConfirm) return;
    const reason = (bulkConfirm.reason || "").trim();
    if (reason.length < 2) { toast.error("Reason required"); return; }
    try {
      const r = await api.post("/admin/users/bulk", {
        action: bulkConfirm.action,
        user_ids: Array.from(selected),
        reason,
      });
      const ok = (r.data.results || []).filter((x) => x.ok).length;
      const skipped = (r.data.results || []).filter((x) => !x.ok).length;
      toast.success(`${ok} ${bulkConfirm.action}d${skipped ? `, ${skipped} skipped` : ""}`);
      setSelected(new Set());
      setBulkConfirm(null);
      fetchList(); fetchSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-admin-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: "Total", value: summary?.total_users, icon: UsersIcon },
          { label: "Active", value: summary?.active_users, icon: CheckCircle },
          { label: "Suspended", value: summary?.suspended_users, icon: ShieldWarning },
          { label: "New today", value: summary?.new_users_today, icon: ClockCounterClockwise },
          { label: "New this week", value: summary?.new_users_this_week, icon: ClockCounterClockwise },
          { label: "Circle participants", value: summary?.prayer_circle_participants, icon: UsersIcon },
          { label: "Personalized prayer", value: summary?.personalized_prayer_users, icon: ListChecks },
        ].map((c, i) => (
          <div key={i} className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0F172A] p-3" data-testid={`users-summary-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon size={16} weight="duotone" className="text-[#D4AF37] mb-1" />
            <p className="font-heading text-xl font-medium text-slate-900 dark:text-white">{c.value ?? "—"}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Search + filters + actions */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white"
            data-testid="users-search-input"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
          data-testid="users-filter-select"
        >
          {FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <label className="text-xs text-slate-500 flex items-center gap-1.5 select-none">
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="users-include-deleted" />
          Include deleted
        </label>
        <button
          onClick={fetchList}
          className="px-3 py-2 rounded-lg border border-slate-200 dark:border-white/10 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
          data-testid="users-refresh-btn"
        >
          <ArrowsClockwise size={14} />
        </button>
        <button
          onClick={downloadCsv}
          className="px-3 py-2 rounded-lg bg-[#D4AF37] text-[#020617] text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
          data-testid="users-export-csv-btn"
        >
          <DownloadSimple size={14} /> Export CSV
        </button>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-2 flex items-center justify-between" data-testid="users-bulk-bar">
          <p className="text-sm text-slate-800 dark:text-slate-100">
            <span className="font-medium">{selected.size}</span> selected
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setBulkConfirm({ action: "suspend", reason: "" })} className="px-3 py-1.5 rounded-md text-xs bg-amber-500/20 text-amber-700 dark:text-amber-300 hover:bg-amber-500/30" data-testid="users-bulk-suspend-btn">Suspend</button>
            <button onClick={() => setBulkConfirm({ action: "reactivate", reason: "" })} className="px-3 py-1.5 rounded-md text-xs bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/30" data-testid="users-bulk-reactivate-btn">Reactivate</button>
            <button onClick={downloadCsv} className="px-3 py-1.5 rounded-md text-xs bg-slate-500/20 text-slate-700 dark:text-slate-200 hover:bg-slate-500/30" data-testid="users-bulk-export-btn">Export selected</button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded-md text-xs text-slate-500 hover:bg-slate-500/10">Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0F172A] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-[#1E293B] text-slate-600 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left w-8">
                  <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleSelectAll} data-testid="users-select-all" />
                </th>
                <SortableTh field="name" sort={sort} order={order} onClick={setSortField}>Name</SortableTh>
                <SortableTh field="email" sort={sort} order={order} onClick={setSortField}>Email</SortableTh>
                <SortableTh field="role" sort={sort} order={order} onClick={setSortField}>Role</SortableTh>
                <SortableTh field="status" sort={sort} order={order} onClick={setSortField}>Status</SortableTh>
                <th className="px-3 py-2 text-left">Lang</th>
                <th className="px-3 py-2 text-left">Circles</th>
                <th className="px-3 py-2 text-left">Journeys</th>
                <SortableTh field="last_active" sort={sort} order={order} onClick={setSortField}>Last active</SortableTh>
                <SortableTh field="created_at" sort={sort} order={order} onClick={setSortField}>Registered</SortableTh>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">No users match.</td></tr>
              )}
              {!loading && items.map((u) => {
                const role = u.role || "user";
                const status = u.status || "active";
                return (
                  <tr
                    key={u.id}
                    className="border-t border-slate-200 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer"
                    onClick={() => setOpenUserId(u.id)}
                    data-testid={`users-row-${u.id}`}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} data-testid={`users-select-${u.id}`} />
                    </td>
                    <td className="px-3 py-2 text-slate-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        {role === "admin" && <Crown size={12} weight="fill" className="text-amber-500" />}
                        <span>{u.name || u.display_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{u.email}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${ROLE_BADGE[role] || ROLE_BADGE.user}`}>{role}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider ${STATUS_BADGE[status] || STATUS_BADGE.active}`}>{status}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{u.language || "—"}</td>
                    <td className="px-3 py-2 text-slate-500">{u.prayer_circle_count ?? 0}</td>
                    <td className="px-3 py-2 text-slate-500">{u.journey_count ?? 0}</td>
                    <td className="px-3 py-2 text-slate-500">{timeAgo(u.last_active_at)}</td>
                    <td className="px-3 py-2 text-slate-500">{fmt(u.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Page {page} of {totalPages} — {total} users</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 rounded border border-slate-200 dark:border-white/10 disabled:opacity-30" data-testid="users-prev-page"><CaretLeft size={14} /></button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-2 py-1 rounded border border-slate-200 dark:border-white/10 disabled:opacity-30" data-testid="users-next-page"><CaretRight size={14} /></button>
        </div>
      </div>

      {/* Bulk confirmation dialog */}
      {bulkConfirm && (
        <Modal onClose={() => setBulkConfirm(null)} title={`${bulkConfirm.action === "suspend" ? "Suspend" : "Reactivate"} ${selected.size} user${selected.size === 1 ? "" : "s"}`}>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
            {bulkConfirm.action === "suspend"
              ? "Suspended users may still log in but cannot create new content, join circles, or share prayers. Admins in your selection will be skipped."
              : "This will restore selected users to active status."}
          </p>
          <textarea
            value={bulkConfirm.reason}
            onChange={(e) => setBulkConfirm({ ...bulkConfirm, reason: e.target.value })}
            placeholder="Reason (required, logged in audit)…"
            className="w-full bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded p-2 text-sm text-slate-900 dark:text-white min-h-[80px]"
            data-testid="users-bulk-reason"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setBulkConfirm(null)} className="px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 text-sm">Cancel</button>
            <button onClick={runBulk} className="px-3 py-1.5 rounded bg-[#D4AF37] text-[#020617] text-sm font-medium" data-testid="users-bulk-confirm">Confirm</button>
          </div>
        </Modal>
      )}

      {/* User detail drawer */}
      {openUserId && (
        <UserDetailDrawer
          uid={openUserId}
          onClose={() => setOpenUserId(null)}
          onChanged={() => { fetchList(); fetchSummary(); }}
        />
      )}
    </div>
  );
}

function SortableTh({ field, sort, order, onClick, children }) {
  const active = sort === field;
  return (
    <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => onClick(field)} data-testid={`users-sort-${field}`}>
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (order === "desc" ? <CaretDown size={10} /> : <CaretUp size={10} />)}
      </span>
    </th>
  );
}

function Modal({ children, title, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className={`bg-white dark:bg-[#0F172A] border border-slate-200 dark:border-white/10 rounded-xl ${wide ? "max-w-4xl" : "max-w-md"} w-full p-5 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        data-testid="users-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-medium text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-white/10"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------- Drawer ----------

function UserDetailDrawer({ uid, onClose, onChanged }) {
  const [user, setUser] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [moderation, setModeration] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [community, setCommunity] = useState(null);
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null); // { type, reason, until }
  const [noteDraft, setNoteDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${uid}`);
      setUser(r.data.user);
      setEngagement(r.data.engagement);
      setModeration(r.data.moderation || []);
    } catch {
      toast.error("Couldn't load user");
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "timeline" && timeline.length === 0)
      api.get(`/admin/users/${uid}/timeline`).then((r) => setTimeline(r.data.items || [])).catch(() => {});
    if (tab === "community" && !community)
      api.get(`/admin/users/${uid}/community`).then((r) => setCommunity(r.data)).catch(() => {});
    if (tab === "notes" && notes.length === 0)
      api.get(`/admin/users/${uid}/notes`).then((r) => setNotes(r.data.items || [])).catch(() => {});
    if (tab === "audit" && audit.length === 0)
      api.get(`/admin/users/${uid}/audit`).then((r) => setAudit(r.data.items || [])).catch(() => {});
  }, [tab, uid, timeline.length, community, notes.length, audit.length]);

  const submitEdit = async () => {
    if (!edit) return;
    const payload = { ...edit };
    if (payload.role && payload.role !== user.role) payload.confirm = true;
    try {
      await api.patch(`/admin/users/${uid}`, payload);
      toast.success("User updated");
      setEdit(null);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const runAction = async () => {
    if (!action) return;
    const reason = (action.reason || "").trim();
    if (reason.length < 2) { toast.error("Reason required"); return; }
    const map = {
      suspend: `/admin/users/${uid}/suspend`,
      block: `/admin/users/${uid}/block`,
      reactivate: `/admin/users/${uid}/reactivate`,
      soft_delete: `/admin/users/${uid}`,
      restore: `/admin/users/${uid}/restore`,
    };
    const url = map[action.type];
    const body = { reason };
    if (action.type === "suspend" && action.until) body.until = action.until;
    try {
      if (action.type === "soft_delete") {
        await api.delete(url, { data: body });
      } else {
        await api.post(url, body);
      }
      toast.success(`User ${action.type.replace("_", " ")}d`);
      setAction(null);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const addNote = async () => {
    if (noteDraft.trim().length < 2) return;
    try {
      const r = await api.post(`/admin/users/${uid}/notes`, { body: noteDraft.trim() });
      setNotes((prev) => [r.data, ...prev]);
      setNoteDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add note");
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-2xl h-full bg-white dark:bg-[#0F172A] border-l border-slate-200 dark:border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="users-detail-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/95 dark:bg-[#0F172A]/95 backdrop-blur border-b border-slate-200 dark:border-white/10 px-5 py-4 flex items-start justify-between">
          {loading || !user ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <div>
              <h3 className="font-heading text-xl font-medium text-slate-900 dark:text-white">{user.name || "—"}</h3>
              <p className="text-xs text-slate-500">{user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider ${STATUS_BADGE[user.status || "active"]}`}>{user.status || "active"}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${ROLE_BADGE[user.role || "user"]}`}>{user.role || "user"}</span>
              </div>
            </div>
          )}
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 dark:hover:bg-white/10"><X size={18} /></button>
        </div>

        {/* Action bar */}
        {user && (
          <div className="px-5 py-3 border-b border-slate-200 dark:border-white/10 flex flex-wrap gap-2">
            <button onClick={() => setEdit({ name: user.name || "", display_name: user.display_name || "", email: user.email || "", language: user.language || "", role: user.role || "user" })} className="px-3 py-1.5 text-xs rounded bg-slate-500/15 text-slate-700 dark:text-slate-200 hover:bg-slate-500/25 flex items-center gap-1.5" data-testid="user-edit-btn"><PencilSimple size={12} />Edit</button>
            {user.status !== "suspended" && user.status !== "blocked" && user.status !== "deleted" && (
              <button onClick={() => setAction({ type: "suspend", reason: "", until: "" })} className="px-3 py-1.5 text-xs rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 hover:bg-amber-500/30 flex items-center gap-1.5" data-testid="user-suspend-btn"><ShieldWarning size={12} />Suspend</button>
            )}
            {user.status !== "blocked" && user.status !== "deleted" && (
              <button onClick={() => setAction({ type: "block", reason: "" })} className="px-3 py-1.5 text-xs rounded bg-red-500/20 text-red-700 dark:text-red-300 hover:bg-red-500/30 flex items-center gap-1.5" data-testid="user-block-btn"><Prohibit size={12} />Block</button>
            )}
            {(user.status === "suspended" || user.status === "blocked") && (
              <button onClick={() => setAction({ type: "reactivate", reason: "" })} className="px-3 py-1.5 text-xs rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1.5" data-testid="user-reactivate-btn"><CheckCircle size={12} />Reactivate</button>
            )}
            {user.status !== "deleted" && (
              <button onClick={() => setAction({ type: "soft_delete", reason: "" })} className="px-3 py-1.5 text-xs rounded bg-red-500/15 text-red-700 dark:text-red-300 hover:bg-red-500/25 flex items-center gap-1.5" data-testid="user-delete-btn"><Trash size={12} />Soft delete</button>
            )}
            {user.status === "deleted" && (
              <button onClick={() => setAction({ type: "restore", reason: "" })} className="px-3 py-1.5 text-xs rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1.5" data-testid="user-restore-btn"><ArrowCounterClockwise size={12} />Restore</button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="px-5 pt-3 flex gap-1 border-b border-slate-200 dark:border-white/10 overflow-x-auto">
          {["profile", "activity", "community", "notes", "moderation", "audit"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t === "activity" ? "timeline" : t)}
              className={`px-3 py-2 text-xs font-medium border-b-2 capitalize whitespace-nowrap ${(tab === t || (t === "activity" && tab === "timeline")) ? "border-[#D4AF37] text-slate-900 dark:text-white" : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
              data-testid={`user-tab-${t}`}
            >
              {t === "activity" ? "Activity" : t}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {tab === "profile" && user && engagement && (
            <>
              <Section title="Identity">
                <Field label="User ID" value={user.id} mono />
                <Field label="Name" value={user.name} />
                <Field label="Display Name" value={user.display_name} />
                <Field label="Email" value={user.email} />
                <Field label="Language" value={user.language} />
                <Field label="Account Created" value={fmt(user.created_at)} />
                <Field label="Last Login" value={user.last_login_at ? `${fmt(user.last_login_at)} (${timeAgo(user.last_login_at)})` : "—"} />
                <Field label="Last Active" value={user.last_active_at ? `${fmt(user.last_active_at)} (${timeAgo(user.last_active_at)})` : "—"} />
              </Section>
              <Section title="Activity">
                <MetricGrid>
                  <Metric label="AI conversations" value={engagement.total_conversations} />
                  <Metric label="Personalized journeys" value={engagement.total_personalized_journeys} />
                  <Metric label="Completed journeys" value={engagement.completed_personalized_journeys} />
                  <Metric label="Circle memberships" value={engagement.circle_memberships} />
                  <Metric label="Circle responses" value={engagement.circle_responses} />
                  <Metric label="Prayer shares" value={engagement.prayer_shares_submitted} />
                  <Metric label="Watch items" value={engagement.watch_items} />
                  <Metric label="Lessons completed" value={engagement.learn_lessons_completed} />
                </MetricGrid>
              </Section>
              <Section title="Latest">
                <Field label="Last journey" value={engagement.last_journey ? `${engagement.last_journey.title} (${fmt(engagement.last_journey.created_at)})` : "—"} />
                <Field label="Last prayer message" value={engagement.last_prayer_message ? fmt(engagement.last_prayer_message.created_at) : "—"} />
              </Section>
              {moderation.length > 0 && (
                <Section title="Recent moderation">
                  <ul className="text-xs space-y-1.5">
                    {moderation.slice(0, 5).map((m) => (
                      <li key={m.id} className="text-slate-600 dark:text-slate-300">
                        <span className="font-medium capitalize">{m.action}</span> — {m.reason} <span className="text-slate-400">· {fmt(m.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}

          {tab === "timeline" && (
            <Section title="Activity timeline">
              {timeline.length === 0 && <p className="text-xs text-slate-500">No events yet.</p>}
              <ul className="space-y-2">
                {timeline.map((e, i) => (
                  <li key={i} className="text-sm text-slate-700 dark:text-slate-200 border-l-2 border-[#D4AF37]/40 pl-3 py-0.5">
                    <p>{e.title}</p>
                    <p className="text-[11px] text-slate-500">{fmt(e.at)} · {e.type}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {tab === "community" && community && (
            <>
              <Section title="Circles joined">
                {community.circles_joined.length === 0 && <p className="text-xs text-slate-500">None.</p>}
                <ul className="space-y-1 text-sm">
                  {community.circles_joined.map((c) => (
                    <li key={c.slug} className="flex items-center justify-between">
                      <a href={`/circles/${c.slug}`} className="text-[#D4AF37] hover:underline">{c.name}</a>
                      <span className="text-[11px] text-slate-500">{c.topic} · {c.member_status}</span>
                    </li>
                  ))}
                </ul>
              </Section>
              <Section title="Circles created">
                {community.circles_created.length === 0 && <p className="text-xs text-slate-500">None.</p>}
                <ul className="space-y-1 text-sm">
                  {community.circles_created.map((c) => (
                    <li key={c.slug}>
                      <a href={`/circles/${c.slug}`} className="text-[#D4AF37] hover:underline">{c.name}</a>
                      <span className="text-[11px] text-slate-500 ml-2">{c.topic} · {fmt(c.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
              <Section title="Prayer shares submitted">
                {community.prayer_shares.length === 0 && <p className="text-xs text-slate-500">None.</p>}
                <ul className="space-y-1 text-sm">
                  {community.prayer_shares.slice(0, 10).map((s) => (
                    <li key={s.id} className="text-slate-700 dark:text-slate-200">
                      <span className="text-xs text-slate-500">{fmt(s.created_at)} · {s.topic} → {s.circle_slug}</span>
                      <p className="text-xs italic">&ldquo;{s.summary}&rdquo;</p>
                    </li>
                  ))}
                </ul>
              </Section>
              <Section title="Engagement">
                <MetricGrid>
                  <Metric label="Responses sent" value={community.responses_sent_count} />
                  <Metric label="Flags reported" value={community.flags_reported_count} />
                  <Metric label="Flags against" value={community.flags_against_count} />
                </MetricGrid>
              </Section>
            </>
          )}

          {tab === "notes" && (
            <Section title="Admin notes (internal only)">
              <div className="flex gap-2 mb-3">
                <input
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNote()}
                  placeholder="Add an admin-only note…"
                  className="flex-1 bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded px-2 py-1.5 text-sm"
                  data-testid="user-note-input"
                />
                <button onClick={addNote} className="px-3 py-1.5 rounded bg-[#D4AF37] text-[#020617] text-sm font-medium" data-testid="user-note-add">Add</button>
              </div>
              {notes.length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li key={n.id} className="rounded border border-slate-200 dark:border-white/10 p-2.5">
                    <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">{n.body}</p>
                    <p className="text-[11px] text-slate-500 mt-1.5">{n.author_name || n.author_email} · {fmt(n.created_at)}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {tab === "moderation" && (
            <Section title="Moderation history">
              {moderation.length === 0 && <p className="text-xs text-slate-500">No actions recorded.</p>}
              <ul className="space-y-2">
                {moderation.map((m) => (
                  <li key={m.id} className="text-sm border-l-2 border-amber-500/40 pl-3">
                    <p className="font-medium capitalize text-slate-800 dark:text-slate-100">{m.action}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-300">{m.reason}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{m.actor_email} · {fmt(m.created_at)}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {tab === "audit" && (
            <Section title="Audit log">
              {audit.length === 0 && <p className="text-xs text-slate-500">No audit entries.</p>}
              <ul className="space-y-1.5 text-xs">
                {audit.map((a) => (
                  <li key={a.id || a.created_at}>
                    <span className="font-mono text-slate-500">{fmt(a.created_at)}</span>
                    {" · "}
                    <span className="font-medium text-slate-700 dark:text-slate-200">{a.action}</span>
                    {" · "}
                    <span className="text-slate-500">{a.actor_email}</span>
                    {a.reason && <span className="text-slate-500"> — {a.reason}</span>}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>

        {/* Edit modal */}
        {edit && (
          <Modal onClose={() => setEdit(null)} title="Edit user">
            <div className="space-y-2.5 text-sm">
              <LabeledInput label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="user-edit-name" />
              <LabeledInput label="Display Name" value={edit.display_name} onChange={(v) => setEdit({ ...edit, display_name: v })} testid="user-edit-display" />
              <LabeledInput label="Email" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} testid="user-edit-email" />
              <LabeledInput label="Language" value={edit.language} onChange={(v) => setEdit({ ...edit, language: v })} testid="user-edit-language" />
              <div>
                <label className="text-xs text-slate-500 block mb-1">Role</label>
                <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} className="w-full bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded p-2 text-sm" data-testid="user-edit-role">
                  <option value="user">User</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
                {edit.role !== user.role && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-300 mt-1">Role change will be confirmed when you save.</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEdit(null)} className="px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 text-sm">Cancel</button>
              <button onClick={submitEdit} className="px-3 py-1.5 rounded bg-[#D4AF37] text-[#020617] text-sm font-medium" data-testid="user-edit-save">Save</button>
            </div>
          </Modal>
        )}

        {/* Action confirmation modal */}
        {action && (
          <Modal onClose={() => setAction(null)} title={`${actionTitle(action.type)} ${user?.name || "user"}`}>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{actionDescription(action.type)}</p>
            {action.type === "suspend" && (
              <div className="mb-3">
                <label className="text-xs text-slate-500 block mb-1">Suspend until (optional)</label>
                <input
                  type="datetime-local"
                  value={action.until || ""}
                  onChange={(e) => setAction({ ...action, until: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  className="w-full bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded p-2 text-sm"
                />
              </div>
            )}
            <textarea
              value={action.reason}
              onChange={(e) => setAction({ ...action, reason: e.target.value })}
              placeholder="Reason (required, logged in audit)…"
              className="w-full bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded p-2 text-sm min-h-[80px]"
              data-testid="user-action-reason"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setAction(null)} className="px-3 py-1.5 rounded border border-slate-200 dark:border-white/10 text-sm">Cancel</button>
              <button onClick={runAction} className="px-3 py-1.5 rounded bg-[#D4AF37] text-[#020617] text-sm font-medium" data-testid="user-action-confirm">Confirm</button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className={`text-sm text-slate-800 dark:text-slate-100 ${mono ? "font-mono text-[11px]" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function MetricGrid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{children}</div>;
}

function Metric({ label, value }) {
  return (
    <div className="rounded border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#1E293B] p-2.5">
      <p className="font-heading text-lg font-medium text-slate-900 dark:text-white">{value ?? 0}</p>
      <p className="text-[10px] text-slate-500">{label}</p>
    </div>
  );
}

function LabeledInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-50 dark:bg-[#1E293B] border border-slate-200 dark:border-white/10 rounded p-2 text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function actionTitle(type) {
  return {
    suspend: "Suspend",
    block: "Block",
    reactivate: "Reactivate",
    soft_delete: "Soft delete",
    restore: "Restore",
  }[type] || type;
}

function actionDescription(type) {
  return {
    suspend: "Suspended users can still log in and read but cannot create new content, join circles, or share prayers.",
    block: "Blocked users cannot log in or call protected APIs. Existing sessions are invalidated.",
    reactivate: "Restores the user to active status.",
    soft_delete: "Soft delete keeps user data for audit. The user can be restored later. No data is hard-deleted.",
    restore: "Restores a previously deleted user.",
  }[type] || "";
}
